import { useEffect } from 'react';
import isEqual from 'lodash/fp/isEqual';
import { createStore, createHook } from 'react-sweet-state';
import { ff } from '@atlassian/jira-feature-flagging';
import { VIEW_MODES, type ViewMode } from '@atlassian/jira-portfolio-3-common';

export const Spotlights = {
	Inactive: 'INACTIVE',
	HeartOfPlan: 'portfolio-3/onboarding/heart-of-plan',
	Scope: 'portfolio-3/onboarding/scope',
	Fields: 'portfolio-3/onboarding/fields',
	Timeline: 'portfolio-3/onboarding/timeline',
	ListMode: 'portfolio-3/onboarding/list-mode',
	Views: 'portfolio-3/onboarding/views',
	ShowAndTell: 'portfolio-3/onboarding/show-and-tell',
	SaveYourChanges: 'portfolio-3/onboarding/save-your-changes',
	FindYourLook: 'portfolio-3/onboarding/find-your-look',
} as const;

export type Spotlights = (typeof Spotlights)[keyof typeof Spotlights];

const TimelineModeSpotlightSteps = Object.values(Spotlights).filter(
	(k) => k !== Spotlights.HeartOfPlan,
); // Take the order of the enums as it is

const ListModeSpotlightSteps = [
	Spotlights.Inactive,
	Spotlights.Scope,
	Spotlights.Fields,
	Spotlights.ListMode,
	Spotlights.Views,
	Spotlights.ShowAndTell,
	Spotlights.SaveYourChanges,
	Spotlights.FindYourLook,
];

const SpotlightSteps = [
	Spotlights.Inactive,
	Spotlights.Views,
	Spotlights.HeartOfPlan,
	Spotlights.ShowAndTell,
	Spotlights.ListMode,
	Spotlights.FindYourLook,
	Spotlights.SaveYourChanges,
];

const store = createStore({
	// Remove "steps" when cleanup 'sploverview-onboarding-tour'
	initialState: { current: 0, steps: TimelineModeSpotlightSteps },
	actions: {
		reset:
			() =>
			({ setState }) =>
				setState({ current: 0 }),
		start:
			() =>
			({ setState }) => {
				setState({ current: 1 });
			},
		next:
			() =>
			({ getState, setState }) => {
				setState({ current: getState().current + 1 });
			},
		setSteps:
			(viewMode: ViewMode) =>
			({ setState, getState }) => {
				if (!ff('sploverview-onboarding-tour')) {
					const { steps } = getState();
					const newSteps =
						viewMode === VIEW_MODES.LIST ? ListModeSpotlightSteps : TimelineModeSpotlightSteps;
					if (!isEqual(steps, newSteps)) {
						setState({
							steps: newSteps,
						});
					}
				}
			},
	},
	name: 'portfolio-3/onboarding/spotlights',
});

export const useCurrentSpotlight = () =>
	createHook(store, {
		selector: ({ current, steps }) => {
			const spotlightSteps = ff('sploverview-onboarding-tour') ? SpotlightSteps : steps;
			const totalStepCount = spotlightSteps.length - 1; // NOTE: All spotlights enums start with an invisible INACTIVE step (step 0)
			return {
				spotlight: spotlightSteps[current],
				currentStepCount: current,
				totalStepCount,
			};
		},
	})();

export const useSpotlightActions = createHook(store, {
	selector: null,
});

// DEPRECATED - Onboarding tour steps will be consistent across view modes
// Remove when cleanup 'sploverview-onboarding-tour'
export const SpotlightStepsUpdater = ({ viewMode }: { viewMode: ViewMode }) => {
	const [, { setSteps }] = useSpotlightActions();
	useEffect(() => {
		setSteps(viewMode);
	}, [viewMode, setSteps]);
	return null;
};

/**
 * This component toggle the header to be visible when the onboarding tour step highlights the table
 */
export const OnboardingHeaderModeUpdater = ({
	headerMode,
	setHeaderMode,
}: {
	headerMode: 'hidden' | 'toolbar' | 'visible';
	setHeaderMode: (mode: 'hidden' | 'toolbar' | 'visible') => void;
}) => {
	const [{ spotlight }] = useCurrentSpotlight();
	useEffect(() => {
		if (headerMode !== 'visible' && spotlight === Spotlights.HeartOfPlan) {
			setHeaderMode('visible');
		}
	}, [spotlight, headerMode, setHeaderMode]);
	return null;
};
